import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** Standard API envelope returned by every ticket-reassign endpoint. */
export interface IReassignResponse<T> {
  status: boolean;
  data: T;
}

/** Ticket counts for one assignee, by status. */
export interface IReassignCounts {
  new: number;
  active: number;
  closed: number;
  other: number;
  total: number;
}

/** Minimal user reference used in history rows. */
export interface IReassignUserRef {
  UserID: number;
  FirstName: string;
  LastName: string;
  EMail: string;
}

/** User account with role and active flag, as returned by preview. */
export interface IReassignUser extends IReassignUserRef {
  RoleType: number;
  Active: number | boolean;
}

/** A ticket owner with counts; deleted owners have no user record, so every name, role and active field is null. */
export interface IReassignSource {
  UserID: number;
  FirstName: string | null;
  LastName: string | null;
  EMail: string | null;
  RoleType: number | null;
  Active: number | boolean | null;
  deleted: boolean;
  counts: IReassignCounts;
}

/** Status values the reassignment can be limited to: 1 New, 2 Active, 3 Closed. */
export type ReassignStatus = 1 | 2 | 3;

/** Request body shared by preview and commit: 1 to 50 source ids, one target; null statuses means every ticket. */
export interface IReassignRequest {
  from_user_ids: number[];
  to_user_id: number;
  statuses: ReassignStatus[] | null;
}

/** Commit request body, carrying the total the user confirmed and the reason for the move. */
export interface IReassignCommitRequest extends IReassignRequest {
  expected_total: number;
  reason: string;
}

/** Preview of what a reassignment would move: per-source counts for the chosen statuses, plus the combined counts. */
export interface IReassignPreview {
  sources: IReassignSource[];
  to: IReassignUser;
  counts: IReassignCounts;
}

/** One history batch created by a commit; there is one per source account. */
export interface IReassignCommitBatch {
  batch_id: number;
  from_user_id: number;
  moved: number;
}

/** Result of a committed reassignment, all or nothing. */
export interface IReassignCommitResult {
  batches: IReassignCommitBatch[];
  moved: number;
  email_sent: boolean;
  email_to: string | null;
}

/** One past reassignment batch from the history endpoint. */
export interface IReassignBatch {
  id: number;
  // Null with from_deleted true when the source account no longer exists; from_user_id still identifies it.
  from_user: IReassignUserRef | null;
  from_user_id: number;
  from_deleted: boolean;
  to_user: IReassignUserRef;
  performed_by: IReassignUserRef;
  undone_by: IReassignUserRef | null;
  statuses: string | null;
  reason: string | null;
  moved: number;
  performed_at: string;
  undone_at: string | null;
  restored: number | null;
}

/** Result of undoing a batch. */
export interface IReassignUndoResult {
  restored: number;
  skipped: number;
  email_sent: boolean;
  email_to: string | null;
}

/** History filters; empty values are left out of the request. Dates are ISO 8601 UTC instants. */
export interface IReassignHistoryFilters {
  date_from?: string | null;
  date_to?: string | null;
  from?: string | null;
  to?: string | null;
  state?: 'done' | 'undone' | null;
  status?: ReassignStatus | null;
}

/** History request: filters plus the page wanted. */
export interface IReassignHistoryQuery extends IReassignHistoryFilters {
  page: number;
  per_page: number;
}

/** Paging block returned alongside a history page. */
export interface IReassignPageInfo {
  total_pages: number;
  page: number;
  per_page: number;
  total: number;
}

/** Raw history response: the rows in data, paging alongside. */
export interface IReassignHistoryResponse extends IReassignResponse<IReassignBatch[]> {
  page: IReassignPageInfo;
}

/** One page of history as handed to the component. */
export interface IReassignHistoryPage {
  rows: IReassignBatch[];
  page: IReassignPageInfo;
}

/** Which hidden tickets to export: only open ones, or every hidden ticket. */
export type AttentionExportScope = 'open' | 'all';

/** Tickets no ticket list shows because the requester, subject or client they point to is gone. */
export interface IReassignHiddenTickets {
  open: { new: number; active: number; total: number };
  closed: number;
  other: number;
  total: number;
  // Open tickets per missing link; one ticket can count under several.
  causes_open: { requester: number; subject: number; client: number };
}

/** Tickets assigned to owners with no user record. */
export interface IReassignDeletedAccounts {
  accounts: number;
  tickets: number;
  open: number;
}

/** Counts for the "Tickets needing attention" card. */
export interface IReassignAttention {
  hidden: IReassignHiddenTickets;
  deleted_accounts: IReassignDeletedAccounts;
}

/** API client for the System Admin bulk ticket reassignment endpoints. */
@Injectable({
  providedIn: 'root'
})
export class TicketReassignService {
  private readonly baseUrl = environment.apiUrl + 'ticket-reassign/';

  /** Injects the HTTP client. */
  constructor(private http: HttpClient) { }

  /** Lists every ticket owner with counts, real accounts first, then deleted ones. */
  getSources(): Observable<IReassignSource[]> {
    return this.http.get<IReassignResponse<IReassignSource[]>>(this.baseUrl + 'sources')
      .pipe(map(res => res.data));
  }

  /** Lists the active, non-restricted attorneys tickets can be moved to. */
  getTargets(): Observable<IReassignUserRef[]> {
    return this.http.get<IReassignResponse<IReassignUserRef[]>>(this.baseUrl + 'targets')
      .pipe(map(res => res.data));
  }

  /** Previews what a reassignment would move without changing anything. */
  preview(request: IReassignRequest): Observable<IReassignPreview> {
    return this.http.post<IReassignResponse<IReassignPreview>>(this.baseUrl + 'preview', request)
      .pipe(map(res => res.data));
  }

  /** Moves the tickets; the server refuses with 409 if the total no longer matches. */
  commit(request: IReassignCommitRequest): Observable<IReassignCommitResult> {
    return this.http.post<IReassignResponse<IReassignCommitResult>>(this.baseUrl + 'commit', request)
      .pipe(map(res => res.data));
  }

  /** Fetches one page of past reassignment batches, newest first, sending only the filters that are set. */
  getHistory(query: IReassignHistoryQuery): Observable<IReassignHistoryPage> {
    const paging = new HttpParams()
      .set('page', String(query.page))
      .set('per_page', String(query.per_page));
    const params = this.filterParams(query, paging);
    return this.http.get<IReassignHistoryResponse>(this.baseUrl + 'history', { params })
      .pipe(map(res => ({ rows: res.data || [], page: res.page })));
  }

  /** Downloads every history row matching the filters as CSV; the full response is returned so the filename header can be read. */
  exportHistory(filters: IReassignHistoryFilters): Observable<HttpResponse<Blob>> {
    return this.http.get(this.baseUrl + 'history/export', {
      params: this.filterParams(filters, new HttpParams()),
      responseType: 'blob',
      observe: 'response'
    });
  }

  /** Counts of hidden tickets and of tickets assigned to deleted accounts. */
  getAttention(): Observable<IReassignAttention> {
    return this.http.get<IReassignResponse<IReassignAttention>>(this.baseUrl + 'attention')
      .pipe(map(res => res.data));
  }

  /** Downloads the hidden tickets as CSV; the full response is returned so the filename header can be read. */
  exportAttention(scope: AttentionExportScope): Observable<HttpResponse<Blob>> {
    return this.http.get(this.baseUrl + 'attention/export', {
      params: new HttpParams().set('scope', scope),
      responseType: 'blob',
      observe: 'response'
    });
  }

  /** Adds each history filter that has a value to the params, trimmed; empty filters are left out. */
  private filterParams(filters: IReassignHistoryFilters, params: HttpParams): HttpParams {
    const optional: [string, string | number | null | undefined][] = [
      ['date_from', filters.date_from],
      ['date_to', filters.date_to],
      ['from', filters.from],
      ['to', filters.to],
      ['state', filters.state],
      ['status', filters.status]
    ];
    for (const [key, value] of optional) {
      const text = value === null || value === undefined ? '' : String(value).trim();
      if (text !== '') {
        params = params.set(key, text);
      }
    }
    return params;
  }

  /** Reverses a batch, leaving tickets that were reassigned since untouched. */
  undo(batchId: number): Observable<IReassignUndoResult> {
    return this.http.post<IReassignResponse<IReassignUndoResult>>(this.baseUrl + 'undo/' + batchId, {})
      .pipe(map(res => res.data));
  }
}
